import React, { useState, useEffect, useMemo } from "react";
import Notification from "../../component/notification";
import { useNavigate, useLocation } from "react-router-dom";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { sendWelcomeEmail, sendCredentialsEmail } from "./email"; // Adjust path as needed

// Static list for education courses
const educationCourses = [
  { label: "B.Tech", value: "btech" },
  { label: "M.Tech", value: "mtech" },
  { label: "B.Sc", value: "bsc" },
  { label: "M.Sc", value: "msc" },
  { label: "MBA", value: "mba" },
  { label: "Other", value: "other" },
];

const StudentForm = ({ onClose, onSubmit, initialValues }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const effectiveInitialValues = initialValues || location.state?.student || {};

  const [form, setForm] = useState({
    name: effectiveInitialValues.name || "",
    email: effectiveInitialValues.email || "",
    phone: effectiveInitialValues.phone || "",
    dob: effectiveInitialValues.dob || "",
    category: effectiveInitialValues.category || "",
    course: effectiveInitialValues.course || "",
    courseStartDate: effectiveInitialValues.courseStartDate || "",
    day_date: effectiveInitialValues.day_date || "",
    college: effectiveInitialValues.college || "",
    educationCourse: effectiveInitialValues.educationCourse || "",
    city: effectiveInitialValues.city || "",
  });
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    visible: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);

  // Validation helpers
  const validateEmail = (email) => /^\S+@\S+\.\S+$/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "category") setForm((prev) => ({ ...prev, course: "" }));
  };

  useEffect(() => {
    if (form.courseStartDate) {
      setForm((prev) => ({ ...prev, day_date: form.courseStartDate }));
    }
  }, [form.courseStartDate]);

  const selectedCategory = useMemo(
    () => categories.find((cat) => cat.value === form.category),
    [categories, form.category]
  );
  const filteredCourses = useMemo(
    () =>
      selectedCategory
        ? courses.filter((c) => c.category === selectedCategory.label)
        : [],
    [courses, selectedCategory]
  );

  const handleSendCredentials = async (studentData) => {
    setEmailLoading(true);
    try {
      const emailResult = await sendCredentialsEmail(studentData);

      if (emailResult.success) {
        setNotification({
          visible: true,
          message: "📧 Credentials email sent successfully!",
          type: "success",
        });
      } else {
        setNotification({
          visible: true,
          message: "⚠️ Email queued but may take a moment to deliver.",
          type: "warning",
        });
      }
    } catch (error) {
      console.error("Error sending credentials email:", error);
      setNotification({
        visible: true,
        message: "❌ Failed to send credentials email. Please try again.",
        type: "failure",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errs = {};

    if (!form.name) errs.name = "Required";
    if (!form.email) errs.email = "Required";
    else if (!validateEmail(form.email)) errs.email = "Invalid email format";
    if (!form.phone) errs.phone = "Required";
    else if (!validatePhone(form.phone)) errs.phone = "Phone must be 10 digits";
    if (!form.category) errs.category = "Required";
    if (!form.course) errs.course = "Required";
    if (!form.courseStartDate) errs.courseStartDate = "Required";
    if (!form.college) errs.college = "Required";
    if (!form.educationCourse) errs.educationCourse = "Required";
    if (!form.city) errs.city = "Required";

    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      setLoading(true);
      try {
        const auth = getAuth();
        const oldDocId = initialValues?.id || location.state?.student?.id;
        let newDocId = oldDocId;
        let isNewStudent = !oldDocId;

        if (!oldDocId) {
          // Create Firebase auth credential for new student
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            form.email,
            form.phone
          );
          newDocId = userCredential.user.uid;
        }

        const docRef = doc(db, "students", newDocId);

        const studentData = {
          ...form,
        };

        if (oldDocId && oldDocId !== newDocId) {
          const oldDocRef = doc(db, "students", oldDocId);
          const oldDocSnap = await getDoc(oldDocRef);
          await setDoc(docRef, {
            ...oldDocSnap.data(),
            ...studentData,
            id: newDocId,
            updatedAt: serverTimestamp(),
          });
          await deleteDoc(oldDocRef);
        } else {
          await setDoc(
            docRef,
            {
              ...studentData,
              id: newDocId,
              [oldDocId ? "updatedAt" : "createdAt"]: serverTimestamp(),
            },
            { merge: true }
          );
        }

        if (isNewStudent) {
          // Small delay to ensure document creation
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const emailResult = await sendWelcomeEmail(form, newDocId);

          if (emailResult.success) {
            setNotification({
              visible: true,
              message: "✅ Student added successfully! Welcome email sent.",
              type: "success",
            });
          } else {
            setNotification({
              visible: true,
              message: `⚠️ Student added successfully! Email error: ${emailResult.error}`,
              type: "warning",
            });
          }
        } else {
          setNotification({
            visible: true,
            message: "✅ Student updated successfully!",
            type: "success",
          });
        }

        setTimeout(() => {
          setNotification({ visible: false, message: "", type: "" });
          setLoading(false);
          navigate("/students", { state: { newStudent: form } });
        }, 3000);
      } catch (error) {
        console.error("Error saving document: ", error);
        let errorMessage = "Please check your internet connection";

        if (error.code === "auth/email-already-in-use") {
          errorMessage = "Email is already registered. Please use a different email.";
        } else if (error.code === "auth/weak-password") {
          errorMessage = "Password is too weak. Phone number must be at least 6 characters.";
        } else if (error.code === "auth/invalid-email") {
          errorMessage = "Invalid email format.";
        }

        setNotification({
          visible: true,
          message: `❌ Error: ${errorMessage}`,
          type: "failure",
        });
        setLoading(false);
      }
    }
  };

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "courses"));
        const coursesData = querySnapshot.docs.map((doc) => ({
          category: doc.data().category,
          label: doc.data().title,
          value: doc.id,
        }));
        setCourses(coursesData);
      } catch (error) {
        console.error("Error fetching courses: ", error);
      }
    };
    fetchCourses();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "courseCategories"));
        const categoriesData = querySnapshot.docs.map((doc) => ({
          label: doc.data().title,
          value: doc.id,
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories: ", error);
      }
    };
    fetchCategories();
  }, []);

  const isExistingStudent = initialValues?.id || location.state?.student?.id;

  return (
    <div
      className="border rounded shadow-sm p-4 bg-white"
      style={{ maxWidth: 600, margin: "2rem auto", fontFamily: "'Inter', sans-serif" }}
    >
      <Notification
        message={notification.message}
        type={notification.type}
        visible={notification.visible}
        onClose={() => setNotification({ visible: false, message: "", type: "" })}
      />

      <div
        style={{
          textAlign: "center",
          marginBottom: "1.5rem",
          fontWeight: 700,
          fontSize: "2rem",
          letterSpacing: 0.5,
          color: "#111827",
        }}
      >
        {isExistingStudent ? "Update Student" : "Add New Student"}
      </div>
      <div
        className="mb-4 text-muted"
        style={{ fontSize: "1rem", color: "#6b7280", textAlign: "center" }}
      >
        Fill in the details to {isExistingStudent ? "update" : "add a new"} student.
      </div>

      {isExistingStudent && form.email && (
        <div
          className="mb-3 p-3"
          style={{
            backgroundColor: "#f9fafb",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h6 style={{ marginBottom: 4, color: "#374151" }}>📧 Email Credentials</h6>
            <small style={{ color: "#6b7280" }}>
              Send login credentials to student's email
            </small>
          </div>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => handleSendCredentials(form)}
            disabled={emailLoading || !form.email}
            style={{
              cursor: emailLoading ? "not-allowed" : "pointer",
              padding: "6px 12px",
              fontSize: "0.875rem",
              borderRadius: 8,
              borderColor: "#7c3aed",
              color: "#7c3aed",
              backgroundColor: emailLoading ? "#ede9fe" : "transparent",
              transition: "background-color 0.3s ease",
            }}
            onMouseEnter={(e) => {
              if (!emailLoading) e.currentTarget.style.backgroundColor = "#7c3aed33";
            }}
            onMouseLeave={(e) => {
              if (!emailLoading) e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {emailLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Sending...
              </>
            ) : (
              "📧 Send Credentials"
            )}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="container" style={{ maxWidth: 500 }}>
        {/* College Name */}
        <div className="mb-3">
          <label htmlFor="college" className="form-label" style={{ fontWeight: 600 }}>
            College Name
          </label>
          <input
            id="college"
            type="text"
            className={`form-control${errors.college ? " is-invalid" : ""}`}
            name="college"
            value={form.college}
            onChange={handleChange}
            autoComplete="organization"
            placeholder="Enter college name"
          />
          {errors.college && <div className="invalid-feedback">{errors.college}</div>}
        </div>

        {/* Education Course */}
        <div className="mb-3">
          <label htmlFor="educationCourse" className="form-label" style={{ fontWeight: 600 }}>
            Education Course
          </label>
          <select
            id="educationCourse"
            className={`form-select${errors.educationCourse ? " is-invalid" : ""}`}
            name="educationCourse"
            value={form.educationCourse}
            onChange={handleChange}
            aria-label="Select education course"
          >
            <option value="">Select a course</option>
            {educationCourses.map((ec) => (
              <option key={ec.value} value={ec.value}>
                {ec.label}
              </option>
            ))}
          </select>
          {errors.educationCourse && (
            <div className="invalid-feedback">{errors.educationCourse}</div>
          )}
        </div>

        {/* City */}
        <div className="mb-3">
          <label htmlFor="city" className="form-label" style={{ fontWeight: 600 }}>
            City
          </label>
          <input
            id="city"
            type="text"
            className={`form-control${errors.city ? " is-invalid" : ""}`}
            name="city"
            value={form.city}
            onChange={handleChange}
            autoComplete="address-level2"
            placeholder="Enter city"
          />
          {errors.city && <div className="invalid-feedback">{errors.city}</div>}
        </div>

        {/* Name */}
        <div className="mb-3">
          <label htmlFor="name" className="form-label" style={{ fontWeight: 600 }}>
            Name
          </label>
          <input
            id="name"
            type="text"
            className={`form-control${errors.name ? " is-invalid" : ""}`}
            name="name"
            value={form.name}
            onChange={handleChange}
            autoFocus
            autoComplete="name"
            placeholder="Enter full name"
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>

        {/* Email */}
        <div className="mb-3">
          <label htmlFor="email" className="form-label" style={{ fontWeight: 600 }}>
            Email
          </label>
          <input
            id="email"
            type="email"
            className={`form-control${errors.email ? " is-invalid" : ""}`}
            name="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            placeholder="Enter email address"
          />
          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
        </div>

        {/* Phone */}
        <div className="mb-3">
          <label htmlFor="phone" className="form-label" style={{ fontWeight: 600 }}>
            Phone
          </label>
          <input
            id="phone"
            type="tel"
            className={`form-control${errors.phone ? " is-invalid" : ""}`}
            name="phone"
            value={form.phone}
            onChange={handleChange}
            autoComplete="tel"
            maxLength={10}
            placeholder="Enter 10-digit phone number"
          />
          {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
        </div>

        {/* Date of Birth */}
        <div className="mb-3">
          <label htmlFor="dob" className="form-label" style={{ fontWeight: 600 }}>
            Date of Birth
          </label>
          <input
            id="dob"
            type="date"
            className="form-control"
            name="dob"
            value={form.dob}
            onChange={handleChange}
            autoComplete="bday"
          />
        </div>

        {/* Course Category */}
        <div className="mb-3">
          <label htmlFor="category" className="form-label" style={{ fontWeight: 600 }}>
            Course Category
          </label>
          <select
            id="category"
            className={`form-select${errors.category ? " is-invalid" : ""}`}
            name="category"
            value={form.category}
            onChange={handleChange}
            aria-label="Select course category"
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          {errors.category && <div className="invalid-feedback">{errors.category}</div>}
        </div>

        {/* Course (filtered) */}
        {form.category && (
          <div className="mb-3">
            <label htmlFor="course" className="form-label" style={{ fontWeight: 600 }}>
              Course
            </label>
            <select
              id="course"
              className={`form-select${errors.course ? " is-invalid" : ""}`}
              name="course"
              value={form.course}
              onChange={handleChange}
              aria-label="Select course"
            >
              <option value="">Select a course</option>
              {filteredCourses.length > 0 ? (
                filteredCourses.map((course) => (
                  <option key={course.value} value={course.value}>
                    {course.label}
                  </option>
                ))
              ) : (
                <option disabled>No courses available for this category</option>
              )}
            </select>
            {errors.course && <div className="invalid-feedback">{errors.course}</div>}
          </div>
        )}

        {/* Course Start Date */}
        <div className="mb-3">
          <label htmlFor="courseStartDate" className="form-label" style={{ fontWeight: 600 }}>
            Course Start Date
          </label>
          <input
            id="courseStartDate"
            type="date"
            className={`form-control${errors.courseStartDate ? " is-invalid" : ""}`}
            name="courseStartDate"
            value={form.courseStartDate}
            onChange={handleChange}
          />
          {errors.courseStartDate && (
            <div className="invalid-feedback">{errors.courseStartDate}</div>
          )}
        </div>

        {/* Form Action Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => (onClose ? onClose() : navigate(-1))}
            disabled={loading}
            style={{ borderRadius: "0.75rem", fontWeight: "600" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-dark"
            disabled={loading}
            style={{ borderRadius: "0.75rem", fontWeight: "600" }}
          >
            {loading && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
            )}
            {(initialValues || location.state?.student) ? "Update Student" : "Add Student"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentForm;